import { mapStringToEnum } from "../../helpers/enum-helper";
import { ApplicationError } from "../../models/errors/application-error";
import { ApplicationDay, ApplicationDeliveryDay } from "../../models/supplier/application-day";
import { ApplicationSupplierDetail } from "../../models/supplier/application-supplier-detail";
import { ApplicationSupplierHeader } from "../../models/supplier/application-supplier-header";
import { ApplicationSupplierList } from "../../models/supplier/application-supplier-list";
import { ApplicationSupplierProductDetail } from "../../models/supplier/application-supplier-product-detail";
import { ApplicationSupplierProductHeader } from "../../models/supplier/application-supplier-product-header";
import { ApplicationSupplierProductsList } from "../../models/supplier/application-supplier-product-list";
import ISupplierService from "./supplier-service-interface";
import client from "../../external/open-api";
import { components } from "../../external/schema/order-link-api-schema";
import { ApplicationSupplierProductUpload } from "../../models/supplier/application-supplier-product-upload";
import { ApplicationSizeUnit } from "../../models/product/application-size-unit";
import BaseService from "../base-service";
import { ApplicationProductOverride } from "../../models/product/application-product-override";
import { ApplicationProductOverrideList } from "../../models/product/application-product-override-list";

export default class SupplierService extends BaseService implements ISupplierService {
  async getSupplier(supplierId: number): Promise<ApplicationSupplierDetail> {
    const { data, error } = await this.withRetry(() => client.GET(
      "/v{version}/api/supplier/{supplierId}",
      {
        params: {
          path: { version: "1", supplierId },
        },
      }
    ));

    if (!data?.data || !data?.success || error) {
      throw new ApplicationError("Failed to fetch supplier");
    }

    return this.mapToSupplierDetail(data.data ?? {});
  }

  async getSuppliers(
    currentPosition: number,
    fetchNext: number,
    fetchAll: boolean,
    searchText: string = "",
  ): Promise<ApplicationSupplierList> {

    const { data, error } = await this.withRetry(() => client.GET("/v{version}/api/supplier", {
      params: {
        path: { version: "1" },
        query: {
          "Paging.CurrentPosition": currentPosition,
          "Paging.FetchNext": fetchNext,
          "Paging.FetchAll": fetchAll,
          "SearchText": searchText
        },
      },
    }));

    if (!data?.data || !data?.success || error) {
      throw new ApplicationError("Failed to fetch suppliers");
    }

    return {
      suppliers: this.mapToSupplierHeaders(data.data.suppliers ?? []),
      loading: false,
      count: data.data?.count || 0,
      currentPosition: data.data?.currentPosition || 0,
    };
  }

  async getOrderSuppliers(orderId: number): Promise<ApplicationSupplierHeader[]> {
    const { error, data } = await this.withRetry(() => client.GET("/v{version}/api/supplier/suppliers/{orderId}", {
      params: {
        path: { version: "1", orderId },  // Path parameter                
      },
    }));

    if (!data || error) {
      throw new ApplicationError("Api call failed fetching order  suppliers");
    }

    return this.mapToSupplierHeaders(data.data?.suppliers ?? []);
  }


  async insertSupplier(
    supplier: ApplicationSupplierDetail
  ): Promise<ApplicationSupplierDetail> {
    const supplierDto = this.mapToSupplierDTO(supplier);
    const { data, error } = await this.withRetry(() => client.POST("/v{version}/api/supplier", {
      params: {
        path: { version: "1" },
      },
      body: {
        ...supplierDto,
      },
    }));

    if (!data?.data || !data?.success || error) {
      throw new ApplicationError("Failed to insert supplier");
    }

    return this.mapToSupplierDetail(data.data ?? {});
  }

  async updateSupplier(
    supplier: ApplicationSupplierDetail
  ): Promise<ApplicationSupplierDetail> {
    const supplierDto = this.mapToSupplierDTO(supplier);
    const { data, error } = await this.withRetry(() => client.PUT(
      "/v{version}/api/supplier/{supplierId}",
      {
        params: {
          path: { version: "1", supplierId: supplier.id },
        },
        body: {
          ...supplierDto,
        },
      }
    ));

    if (!data?.data || !data?.success || error) {
      throw new ApplicationError("Failed to update supplier");
    }

    return this.mapToSupplierDetail(data.data ?? {});
  }


  async getProductSuppliers(
  branchId: number,
  productIds: number[]
): Promise<Record<number, ApplicationSupplierProductHeader[]>> {
  const { data, error } = await this.withRetry(() =>
    client.GET(
      "/v{version}/api/supplier/supplierproduct/branch/{branchId}",
      {
        params: {
          path: {
            version: "1",
            branchId,
          },
          query: {
            productIds,
          },
        },
      }
    )
  );

  if (!data?.data || !data?.success || error) {
    throw new ApplicationError("Failed to fetch supplier products");
  }

  return Object.fromEntries(
    Object.entries(data.data).map(([productId, suppliers]) => [
      Number(productId),
      suppliers!.map(x => this.mapToSupplierProductHeader(x)),
    ])
  );
}
  async getProduct(
    supplierProductId: number
  ): Promise<ApplicationSupplierProductDetail> {
    const { data, error } = await this.withRetry(() => client.GET(
      "/v{version}/api/supplier/supplierproduct/{supplierProductId}",
      {
        params: {
          path: { version: "1", supplierProductId },
        },
      }
    ));

    if (!data?.data || !data?.success || error) {
      throw new ApplicationError("Failed to fetch supplier product");
    }

    return {
      ...this.mapToSupplierProductHeader(data.data ?? {}),
      created: data.data.created ? new Date(data.data.created) : new Date(),
    };
  }

  async getProductComparisons(productId: number): Promise<ApplicationSupplierProductHeader[]> {
    const { data, error } = await this.withRetry(() => client.GET(
      "/v{version}/api/supplier/supplierproduct/product/{productId}/comparison",
      {
        params: {
          path: { version: "1", productId },
        },
      }
    ));

    if (!data?.data || !data?.success || error) {
      throw new ApplicationError("Failed to fetch product comparisons");
    }

    return this.mapToSupplierProductHeaders(data.data ?? []);
  }

  async getProducts(
    supplierId: number,
    currentPosition: number,
    fetchNext: number,
    fetchAll: boolean,
    unmappedOnly: boolean,
    searchText?: string
  ): Promise<ApplicationSupplierProductsList> {
    const { data, error } = await this.withRetry(() => client.GET(
      "/v{version}/api/supplier/{supplierId}/supplierproduct",
      {
        params: {
          path: { version: "1", supplierId },
          query: {
            "Paging.CurrentPosition": currentPosition,
            "Paging.FetchNext": fetchNext,
            "Paging.FetchAll": fetchAll,
            "SearchText": searchText,
            "UnmappedOnly": unmappedOnly
          },
        },
      }
    ));

    if (!data?.data || !data?.success || error) {
      throw new ApplicationError("Failed to fetch supplier products");
    }

    return {
      products: this.mapToSupplierProductHeaders(data.data.products ?? []),
      loading: false,
      count: data.data?.count || 0,
      currentPosition: data.data?.currentPosition || 0,
    };
  }

  async insertProductPrice(
    supplierProductId: number,
    price: number
  ): Promise<void> {
    const { data, error } = await this.withRetry(() => client.POST(
      "/v{version}/api/supplier/supplierproduct/{supplierProductId}/price",
      {
        params: {
          path: { version: "1", supplierProductId },
        },
        body: {
          price,
        },
      }
    ));

    if (!data?.data || !data?.success || error) {
      throw new ApplicationError("Failed to insert product price");
    }
  }

  async updateProduct(
    product: ApplicationSupplierProductDetail
  ): Promise<ApplicationSupplierProductDetail> {
    const request = this.mapToSupplierProductDTO(product);

    const { data, error } = await this.withRetry(() => client.PUT(
      "/v{version}/api/supplier/supplierproduct/{supplierProductId}",
      {
        params: {
          path: { version: "1", supplierProductId: product.id },
        },
        body: {
          ...request,
        },
      }
    ));

    if (!data?.data || !data?.success || error) {
      throw new ApplicationError("Failed to update supplier product");
    }

    return {
      ...this.mapToSupplierProductHeader(data.data ?? {}),
      created: data.data.created ? new Date(data.data.created) : new Date(),
    };
  }

  async insertProduct(
    product: ApplicationSupplierProductDetail
  ): Promise<ApplicationSupplierProductDetail> {
    const request = this.mapToSupplierProductDTO(product);

    const { data, error } = await this.withRetry(() => client.POST(
      "/v{version}/api/supplier/supplierproduct",
      {
        params: {
          path: { version: "1" },
        },
        body: {
          ...request,
        },
      }
    ));

    if (!data?.data || !data?.success || error) {
      throw new ApplicationError("Failed to insert supplier product");
    }

    return {
      ...this.mapToSupplierProductHeader(data.data ?? {}),
      created: data.data.created ? new Date(data.data.created) : new Date(),
    };
  }

  async bulkUploadProducts(
    supplierId: number,
    products: ApplicationSupplierProductUpload[]
  ): Promise<ApplicationSupplierProductsList | null> {

    const updatedProducts = products.map((product) => ({
      name: product.name,
      reference: product.reference,
      price: product.price,
      size: product.size,
      sizeUnit: product.sizeunit,
      plu: product.plu,
      origin: product.origin,
      minimumOrderQuantity: product.minimumOrderQuantity,
      openPricing: product.openPricing,
      productInformation: product.information,
    }));

    const { data, error } = await this.withRetry(() => client.POST(
      "/v{version}/api/supplier/supplierproduct/bulk",
      {
        params: {
          path: { version: "1" },
        },
        body: {
          supplierId,
          products: updatedProducts,
        },
      }
    ));

    if (error) {
      throw new ApplicationError("Failed to bulk upload products");
    }
    if (!data?.data || !data?.success) {
      throw new ApplicationError("Unexpected response format");
    }

    return {
      products: this.mapToSupplierProductHeaders(data.data.products ?? []),
      loading: false,
      count: data.data?.count || 0,
      currentPosition: data.data?.currentPosition || 0,
    };
  }

  async restoreFromHistory(
    supplierProductId: number
  ): Promise<ApplicationSupplierProductDetail> {
    const { data, error } = await this.withRetry(() => client.POST(
      "/v{version}/api/supplier/supplierproduct/{supplierProductId}/restore",
      {
        params: {
          path: { version: "1", supplierProductId },
        },
      }
    ));

    if (!data?.data || !data?.success || error) {
      throw new ApplicationError("Failed to fetch supplier product");
    }

    return {
      ...this.mapToSupplierProductHeader(data.data ?? {}),
      created: data.data.created ? new Date(data.data.created) : new Date(),
    };
  }


  async updateProductSizeUnit(
    productId: number,
    newSizeUnit: ApplicationSizeUnit
  ): Promise<ApplicationSupplierProductHeader[]> {

    const { data, error } = await this.withRetry(() => client.PATCH("/v{version}/api/supplier/supplierproducts/sizeunits",
      {
        params: {
          path: { version: "1" },
        },
        body: {
          productId,
          newSizeUnit
        },
      }
    ));

    if (!data?.data || !data?.success || error) {
      throw new ApplicationError("Failed to update product size unit");
    }

    return this.mapToSupplierProductHeaders(data.data ?? []);
  }

  async deleteProduct(
    supplierProductId: number,
  ): Promise<void> {
    const { data, error } = await this.withRetry(() => client.DELETE(
      "/v{version}/api/supplier/supplierproduct/{supplierProductId}",
      {
        params: {
          path: { version: "1", supplierProductId },
        },
      }
    ));

    if (!data?.data || !data?.success || error) {
      throw new ApplicationError("Failed to delete product");
    }
  }

  async insertProductOverride(productId: number, supplierId: number): Promise<ApplicationProductOverride | undefined> {
    const { data, error } = await this.withRetry(() => client.POST(
      "/v{version}/api/supplier/supplierproduct/{productId}/override",
      {
        params: {
          path: { version: "1", productId },
        },
        body: {
          supplierId
        }
      }
    ));

    if (!data?.success || error) {
      throw new ApplicationError("Unable to set product as override");
    }
    if (!data.data) {
      return undefined;
    }

    return this.mapOverride(data.data)
  }

  async deleteProductOverride(id: number): Promise<void> {
    const { data, error } = await this.withRetry(() => client.DELETE(
      "/v{version}/api/supplier/supplierproduct/override/{id}",
      {
        params: {
          path: { version: "1", id },
        },
      }
    ));

    if (!data?.success || error) {
      throw new ApplicationError("Unable to remove product override");
    }
  }

  async getOverrides(
    currentPosition: number,
    fetchNext: number,
    fetchAll: boolean): Promise<ApplicationProductOverrideList> {
    const { data, error } = await this.withRetry(() => client.GET(
      "/v{version}/api/supplier/supplierproduct/override",
      {
        params: {
          path: { version: "1" },
          query: {
            CurrentPosition: currentPosition,
            FetchNext: fetchNext,
            FetchAll: fetchAll,
          }
        },
      }
    ));
   
    if (!data?.success || error) {
      throw new ApplicationError("Unable to set product as override");
    }
    if (!data.data) {
      return {
        overrides: [],
        loading: false,
        count: 0,
        currentPosition: 0,
      };
    }

    return {
      overrides: data.data.overrides?.map((x) => this.mapOverride(x)).filter((x): x is ApplicationProductOverride => x !== undefined) || [],
      loading: false,
      count: data.data?.count || 0,
      currentPosition: data.data?.currentPosition || 0,
    };
  }

  private mapToSupplierDetail(
    supplier: components["schemas"]["SupplierDTO"]
  ): ApplicationSupplierDetail {
    return {
      id: supplier.id ?? 0,
      name: supplier.name ?? "",
      noOfProducts: supplier.noOfProducts ?? 0,
      updated: supplier.updated ? new Date(supplier.updated) : new Date(),
      addressLine1: supplier.addressLine1 ?? "",
      addressLine2: supplier.addressLine2 ?? "",
      addressLine3: supplier.addressLine3 ?? "",
      city: supplier.city ?? "",
      postcode: supplier.postcode ?? "",
      phone: supplier.phone ?? "",
      email: supplier.email ?? "",
      apiIntegration: supplier.apiIntegration ?? false,
      whatsappIntegration: supplier.whatsappIntegration ?? false,
      emailIntegration: supplier.emailIntegration ?? false,
      departmentIds: supplier.departmentIds ?? [],
      orderCutOffTime: supplier.orderCutOffTime ?? "",
      branches: supplier.branches?.map((branch) => ({
        id: branch?.id ?? 0,
        name: branch?.name ?? "",
        days: branch.days?.map((d) => ApplicationDay[d as keyof typeof ApplicationDay]),
        shipToNumber: branch.shipToNumber,
        billToNumber: branch.billToNumber,
        accountNumber: branch.accountNumber,
      })) ?? [],
      requiresOrderApproval: supplier.requiresOrderApproval ?? false,
      minimumQuantity: supplier.minimumQuantity ?? 0,
      minimumValue: supplier.minimumValue ?? 0,
      productOrderingType: supplier.productOrderingType ?? "Cases",
      minimumPallets: supplier.minimumPallets,
    };
  }

  private mapToSupplierDTO(
    supplier: ApplicationSupplierDetail
  ): components["schemas"]["SupplierDTO"] {
    return {
      id: supplier?.id ?? 0,
      name: supplier.name,
      addressLine1: supplier.addressLine1,
      addressLine2: supplier.addressLine2,
      addressLine3: supplier.addressLine3,
      city: supplier.city,
      postcode: supplier.postcode,
      phone: supplier.phone,
      email: supplier.email,
      apiIntegration: supplier.apiIntegration,
      whatsappIntegration: supplier.whatsappIntegration,
      emailIntegration: supplier.emailIntegration,
      departmentIds: supplier.departmentIds,
      orderCutOffTime: supplier.orderCutOffTime,
      branches: supplier.branches,
      requiresOrderApproval: supplier.requiresOrderApproval,
      minimumQuantity: supplier.minimumQuantity,
      minimumValue: supplier.minimumValue,
      productOrderingType: supplier.productOrderingType,
      minimumPallets: supplier.minimumPallets
    };
  }

  private mapToSupplierProductDTO(
    product: ApplicationSupplierProductDetail
  ): components["schemas"]["SupplierProductDTO"] {
    return {
      id: product.id ?? 0,
      reference: product.reference,
      supplierId: product.supplierId,
      productId: product.productId,
      openPricing: product.openPricing,
      name: product.name,
      size: product.size,
      sizeUnit: product.sizeUnit,
      minimumOrderQuantity: product.minimumOrderQuantity,
      active: product.active,
      price: product.price ?? 0,
      productInformation: product.information,
      layerSize: product.layerSize,
      palletSize: product.palletSize
    };
  }

  private mapToSupplierHeaders(
    suppliers: components["schemas"]["SupplierDTO"][] | undefined
  ): ApplicationSupplierHeader[] {
    return suppliers?.map(this.mapToSupplierHeader) || [];
  }

  private mapToSupplierHeader(
    supplier: components["schemas"]["SupplierDTO"]
  ): ApplicationSupplierHeader {
    return {
      id: supplier.id ?? 0,
      name: supplier.name ?? "",
      noOfProducts: supplier.noOfProducts ?? 0,
      addressLine1: supplier.addressLine1 ?? "",
      addressLine2: supplier.addressLine2 ?? "",
      addressLine3: supplier.addressLine3 ?? "",
      city: supplier.city ?? "",
      postcode: supplier.postcode ?? "",
      phone: supplier.phone ?? "",
      email: supplier.email ?? "",
      updated: supplier.updated ? new Date(supplier.updated) : new Date(),
      branches: supplier.branches?.map((branch) => ({ id: branch?.id ?? 0, name: branch?.name ?? "" })) ?? [],
      requiresOrderApproval: supplier.requiresOrderApproval ?? false,
    };
  }

  private mapToSupplierProductHeaders(
    products: components["schemas"]["SupplierProductDTO"][] | undefined
  ): ApplicationSupplierProductHeader[] {
    return products?.map(this.mapToSupplierProductHeader) || [];
  }
  private mapToSupplierProductHeader = (
    product: components["schemas"]["SupplierProductDTO"]
  ) => {
    return {
      id: product.id ?? 0,
      reference: product.reference ?? "",
      supplierId: product.supplierId ?? 0,
      supplierName: product.supplierName ?? "",
      productId: product.productId,
      name: product.name ?? "",
      description: product.description ?? "",
      image: product.image ?? "",
      plu: product.plu ?? "",
      size: product.size ?? 0,
      sizeUnit: mapStringToEnum(ApplicationSizeUnit, product.sizeUnit ?? "Unknown"),
      origin: product.origin ?? "",
      updated: product.updated ? new Date(product.updated) : new Date(),
      active: product.active ?? false,
      price: product.price ?? 0,
      openPricing: product.openPricing ?? false,
      hasHistory: product.hasHistory ?? false,
      minimumOrderQuantity: product.minimumOrderQuantity,
      layerSize: product.layerSize,
      palletSize: product.palletSize,
      deliveryDay: this.mapDeliveryDay(product.deliveryDayDTO),
      productOverride: this.mapOverride(product.productSupplierOverride),
      information: product?.productInformation ?? {},
      className: product.productSupplierOverride ? "highlight" : ""
    };
  };

  private mapDeliveryDay(data: components["schemas"]["DeliveryDayDTO"] | undefined): ApplicationDeliveryDay | undefined {
    if (!data) {
      return undefined;
    }

    return {
      date: data?.date ? new Date(data?.date) : new Date(),
      day: mapStringToEnum(ApplicationDay, data?.day ?? "Monday")
    }
  }


  private mapOverride(data: components["schemas"]["ProductOverrideDTO"] | undefined): ApplicationProductOverride | undefined {
    if (!data) {
      return undefined;
    }

    return {
      id: data.id ?? 0,
      productId: data.productId ?? 0,
      productName: data?.productName ?? "",
      productImage: data?.productImage ?? "", 
      supplierId: data?.supplierId ?? 0,
      supplierName: data?.supplierName ?? "",
      expiresOn: new Date(data?.expiresOn ?? "1990-01-01")
    }
  }
}

